import React, { useEffect, useState } from 'react';
import SelectField from '../components/SelectField';
import {
  FaCheck,
  FaChevronDown,
  FaClipboardCheck,
  FaEnvelope,
  FaHistory,
  FaPen,
  FaSearch,
  FaTimes,
  FaUserFriends,
  FaUserPlus,
  FaUserTie,
  FaUsers
} from 'react-icons/fa';
import { TableRowsSkeleton } from '../components/LoadingState';
import { listCognitoUsers, listCognitoGroups } from '../api/users';
import {
  enableUser,
  disableUser,
  resetUserPassword,
  deleteUser,
  createUser,
  updateUser,
  setUserRole
} from '../api/adminUsers';
import { listAgentsWithEmails, updateAgentEmail, listActivityActors, getActorActivity } from '../api/agentEmails';
import ConfirmationModal from '../components/ConfirmationModal';
import ActionDropdown from '../components/ActionDropdown';
import Modal from '../components/Modal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import UserFormModal from '../components/UserFormModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import RoleModal from '../components/RoleModal';
import AgentEmailModal from '../components/AgentEmailModal';
import useDebounce from '../hooks/useDebounce';

// Search input with an inline clear button, used by both the Users and Agent Emails tabs.
function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative flex-1">
      <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input
        className="input-field !pl-10 w-full"
        style={{ paddingRight: value ? '2rem' : undefined }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          onClick={() => onChange('')}
        >
          <FaTimes style={{ fontSize: '0.75rem' }} />
        </button>
      )}
    </div>
  );
}

// Canonical CloudZA chip recipe with dark-safe tints.
const CHIP_BASE = 'inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border';
const CHIP_TINTS = {
  cloudza: 'bg-cloudza-50 text-cloudza-700 border-cloudza-100',
  coral: 'bg-coral-50 text-coral-700 border-coral-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  gray: 'bg-gray-100 text-gray-600 border-gray-200',
};

function Chip({ tint = 'gray', className = '', children }) {
  return (
    <span className={`${CHIP_BASE} ${CHIP_TINTS[tint] || CHIP_TINTS.gray} ${className}`}>
      {children}
    </span>
  );
}

// Per-Cognito-group chip tints (anything unknown falls back to gray).
const GROUP_TINT = {
  SuperAdmin: 'cloudza',
  Admin: 'coral',
  QAAgent: 'amber',
  CallAgent: 'gray',
};

// Identity tile with the initials of up to the first two words of a name.
const initialsOf = (name) =>
  String(name || '')
    .trim()
    .split(/[\s\-_]+/)
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join('') || '?';

function IdentityTile({ name }) {
  return (
    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-cloudza-500 to-cloudza-700 text-white text-xs font-bold flex items-center justify-center uppercase flex-shrink-0 shadow-sm">
      {initialsOf(name)}
    </span>
  );
}

const ROLE_META = {
  GM: { label: 'General Manager', icon: FaUserTie, tint: 'cloudza' },
  JM: { label: 'Junior Manager', icon: FaUserFriends, tint: 'amber' },
  QA: { label: 'QA Assessor', icon: FaClipboardCheck, tint: 'coral' },
};

function RoleBadge({ role }) {
  const meta = role && ROLE_META[role];
  if (!meta) {
    return <Chip tint="gray">No Role</Chip>;
  }
  const Icon = meta.icon;
  return (
    <Chip tint={meta.tint} className="gap-1.5 whitespace-nowrap">
      <Icon style={{ fontSize: '0.7rem' }} />
      {meta.label}
    </Chip>
  );
}

// One component, two pages: /users renders only the Cognito-users section, and
// /agents (via the AgentManagement wrapper below) renders the agent-email and
// activity-log sections. They stay in one file because they share the modals,
// the prefill flow (create a console user from an agent's email), and the
// lookup of existing user emails.
const UserManagement = ({ setAlert, userEmail, section = 'users' }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState(section === 'agents' ? 'agents' : 'users');

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [paginationToken, setPaginationToken] = useState();
  const [showCreate, setShowCreate] = useState(false);
  const [sortField, setSortField] = useState('Username');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [confirmation, setConfirmation] = useState({ open: false, title: '', message: '' });
  const [editUserModal, setEditUserModal] = useState(null);
  const [resetUserModal, setResetUserModal] = useState(null);
  const [roleModal, setRoleModal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState('');

  // Agents state
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const debouncedAgentSearch = useDebounce(agentSearch, 300);
  const [agentSortField, setAgentSortField] = useState('name');
  const [agentSortDirection, setAgentSortDirection] = useState('asc');
  const [agentPage, setAgentPage] = useState(1);
  const [filterEmail, setFilterEmail] = useState('all');
  const [editAgent, setEditAgent] = useState(null);
  const [preFillData, setPreFillData] = useState(null);

  // Activity log state
  const [actors, setActors] = useState([]);
  const [actorsLoading, setActorsLoading] = useState(false);
  const [selectedActor, setSelectedActor] = useState('');
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadUsers();
    (async () => {
      const grps = await listCognitoGroups();
      if (cancelled) return;
      setGroups(grps.groups || []);
    })();
    return () => { cancelled = true; };
  }, []);
  // console.log(`Groups: ${groups}`);

  useEffect(() => {
    if (activeTab === 'agents' && agents.length === 0) {
      loadAgents();
    }
    if (activeTab === 'activity' && actors.length === 0) {
      loadActors();
    }
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
    loadUsers();
  }, [debouncedSearch, selectedGroup]);


  const normalizeUser = (u) => {
    if (u.Username && u.Attributes) {
      return {
        ...u,
        UserStatus: u.UserStatus || u.userStatus || u.status
      };
    }

    const normalGroups = (() => {
      if (!u.groups) return undefined;
      if (Array.isArray(u.groups)) {
        return u.groups
          .map(g => {
            if (typeof g === 'string') return g;
            if (typeof g === 'object' && g) {
              return g.groupName || g.name || g.GroupName || '';
            }
            return '';
          })
          .filter(Boolean)
          .join(',');
      }
      if (typeof u.groups === 'object') {
        return u.groups.groupName || u.groups.name || u.groups.GroupName || '';
      }
      return u.groups;
    })();

    return {
      Username: u.username || u.Username,
      Enabled: typeof u.enabled === 'boolean' ? u.enabled : true,
      UserStatus: u.UserStatus || u.userStatus || u.status,
      Attributes: [
        u.email ? { Name: 'email', Value: u.email } : null,
        normalGroups ? { Name: 'custom:groups', Value: normalGroups } : null,
      ].filter(Boolean),
    };
  };

  const loadUsers = async (tokenArg) => {
    setUsersLoading(true);
    try {
      const data = await listCognitoUsers(
        selectedGroup || null,
        debouncedSearch || null,
        tokenArg
      );
      let loaded = data.Users || data.users || data;
      if (Array.isArray(loaded)) {
        loaded = loaded.map(normalizeUser);
      } else if (loaded && Array.isArray(loaded.Users)) {
        loaded = loaded.Users.map(normalizeUser);
      } else {
        loaded = [];
      }
      if (tokenArg) {
        setUsers((prev) => [...prev, ...loaded]);
      } else {
        setUsers(loaded);
      }
      setPaginationToken(
        data.paginationToken || data.NextToken || data.nextToken || null
      );
    } catch (e) {
      console.error('Failed to load users', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load users" });
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAgents = async () => {
    setAgentsLoading(true);
    try {
      const data = await listAgentsWithEmails();
      setAgents(data);
    } catch (e) {
      console.error('Failed to load agents', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load agents" });
      }
    } finally {
      setAgentsLoading(false);
    }
  };

  const loadActors = async () => {
    setActorsLoading(true);
    try {
      const data = await listActivityActors();
      setActors(data || []);
    } catch (e) {
      console.error('Failed to load activity actors', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load the activity log" });
      }
    } finally {
      setActorsLoading(false);
    }
  };

  const handleSelectActor = async (actorEmail) => {
    setSelectedActor(actorEmail);
    if (!actorEmail) {
      setActivities([]);
      return;
    }
    setActivitiesLoading(true);
    try {
      const data = await getActorActivity(actorEmail);
      setActivities(data.activities || []);
    } catch (e) {
      console.error('Failed to load actor activity', e);
      if (setAlert) {
        setAlert({ variant: 'error', heading: 'Error', text: "Couldn't load this admin's activity" });
      }
    } finally {
      setActivitiesLoading(false);
    }
  };

  const handleAgentSort = (field) => {
    if (agentSortField === field) {
      setAgentSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setAgentSortField(field);
      setAgentSortDirection('asc');
    }
  };

  const handleAgentPageChange = (dir) => {
    setAgentPage((p) => {
      const total = Math.ceil(sortedAgents.length / PAGE_SIZE) || 1;
      const next = p + dir;
      if (next < 1) return 1;
      if (next > total) return total;
      return next;
    });
  };

  const handleUpdateAgentEmail = async (agentName, email, role, gmUsername) => {
    try {
      await updateAgentEmail(agentName, email, role, gmUsername, userEmail);
      setEditAgent(null);
      setConfirmation({
        open: true,
        title: 'Email Updated',
        message: email
          ? `Email for ${agentName} has been set to ${email}`
          : `Email for ${agentName} has been removed`
      });
      loadAgents();
    } catch (err) {
      console.error('Failed to update agent email', err);
      setConfirmation({
        open: true,
        title: 'Error',
        message: err.message || "Couldn't update agent email"
      });
    }
  };


  // Filter and sort agents
  const filteredAgents = agents.filter((a) => {
    const matchesSearch =
      a.name.toLowerCase().includes(debouncedAgentSearch.toLowerCase()) ||
      (a.email && a.email.toLowerCase().includes(debouncedAgentSearch.toLowerCase()));
    const matchesEmailFilter =
      filterEmail === 'all' ||
      (filterEmail === 'with' && a.email) ||
      (filterEmail === 'without' && !a.email);
    return matchesSearch && matchesEmailFilter;
  });

  const sortedAgents = [...filteredAgents].sort((a, b) => {
    let aVal, bVal;
    switch (agentSortField) {
      case 'email':
        aVal = a.email || '';
        bVal = b.email || '';
        break;
      case 'updatedAt':
        aVal = a.updatedAt || '';
        bVal = b.updatedAt || '';
        break;
      default:
        aVal = a.name;
        bVal = b.name;
    }
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    if (aVal < bVal) return agentSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return agentSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const agentTotalPages = Math.ceil(sortedAgents.length / PAGE_SIZE) || 1;
  const paginatedAgents = sortedAgents.slice((agentPage - 1) * PAGE_SIZE, agentPage * PAGE_SIZE);
  const agentsWithEmail = agents.filter((a) => a.email).length;
  const existingUserEmails = new Set(
    users.map(u => 
      u.Attributes?.find(a => a.Name === 'email')?.Value?.toLowerCase()
    ).filter(Boolean)
  );

  const handleEnable = (u) => {
    setConfirmAction({
      title: 'Enable User',
      message: `Enable ${u.Username}?`,
      onConfirm: async () => {
        await enableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };

  const handleDisable = (u) => {
    setConfirmAction({
      title: 'Disable User',
      message: `Disable ${u.Username}?`,
      onConfirm: async () => {
        await disableUser(u.Username);
        setConfirmAction(null);
        loadUsers();
      }
    });
  };


  const handleReset = (u) => {
    setResetUserModal(u);
  };

  const handleDelete = (u) => {
    setDeleteUserName(u.Username);
  };

  const confirmDeleteUser = async () => {
    await deleteUser(deleteUserName);
    setDeleteUserName('');
    loadUsers();
  };

  const handleSetRole = (u) => {
    setRoleModal(u);
  };

  const handleEdit = (u) => {
    setEditUserModal(u);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (dir) => {
    setPage((p) => {
      const total = Math.ceil(sorted.length / PAGE_SIZE) || 1;
      const next = p + dir;
      if (next < 1) return 1;
      if (next > total) return total;
      return next;
    });
  };


  const filtered = users.filter(u =>
    u.Username.toLowerCase().includes(search.toLowerCase()) ||
    (u.Attributes?.find(a => a.Name === 'email')?.Value || '').toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let aVal;
    let bVal;
    switch (sortField) {
      case 'Email':
        aVal = a.Attributes?.find(a => a.Name === 'email')?.Value || '';
        bVal = b.Attributes?.find(a => a.Name === 'email')?.Value || '';
        break;
      case 'Status':
        aVal = a.UserStatus;
        bVal = b.UserStatus;
        break;
      case 'Username':
      default:
        aVal = a.Username;
        bVal = b.Username;
    }
    aVal = String(aVal).toLowerCase();
    bVal = String(bVal).toLowerCase();
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE) || 1;
  const paginated = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
// Filter agents who have the role 'General Manager' or 'GM'
  const generalManagers = agents.filter(agent => 
    agent.role === 'General Manager' || agent.role === 'GM'
  );

  const JMListDropdown = ({ jms }) => {
    const [isOpen, setIsOpen] = React.useState(false);

    return (
      <div className="relative inline-block text-left mt-1">
        <button
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click events
            setIsOpen(!isOpen);
          }}
          className="flex items-center text-[10px] font-medium text-cloudza-700 bg-cloudza-50 hover:bg-cloudza-100 px-2 py-0.5 rounded-full border border-cloudza-100 transition-colors"
        >
          Managing: {jms.length} JM(s)
          <FaChevronDown className={`ml-1 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ fontSize: '0.6rem' }} />
        </button>

        {isOpen && (
          <>
            {/* Transparent overlay to close when clicking outside */}
            <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)}></div>
            
            <div className="absolute left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-1">
              <div className="px-3 py-1 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                Attached JMs
              </div>
              <div className="max-h-32 overflow-y-auto">
                {jms.map((jm, idx) => (
                  <div key={idx} className="px-3 py-2 hover:bg-gray-50 flex flex-col border-b border-gray-100 last:border-0">
                    <span className="text-xs font-semibold text-gray-800">{jm.username}</span>
                    <span className="text-[10px] text-gray-500">{jm.email}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{section === 'agents' ? 'Agent Management' : 'User Management'}</h1>
          <p className="text-gray-500 text-sm mt-1">
            {section === 'agents'
              ? 'Manage agent emails, roles, hierarchy, and audit activity'
              : 'Manage console users and their roles'}
          </p>
        </div>
      </div>

      {/* Tabs — only the agents page has more than one section (its own audit
          trail); the users page is single-purpose so it shows no tab bar. */}
      {section === 'agents' && (
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8" data-tour="user-tabs">
            <button
              onClick={() => setActiveTab('agents')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'agents'
                  ? 'border-cloudza-600 text-cloudza-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200'
              }`}
            >
              <FaUserTie />
              Agent Emails
              {agentsWithEmail > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cloudza-50 text-cloudza-700 text-xs font-semibold">
                  {agentsWithEmail}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'activity'
                  ? 'border-cloudza-600 text-cloudza-700'
                  : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200'
              }`}
            >
              <FaHistory />
              Activity Log
            </button>
          </nav>
        </div>
      )}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 w-full">
          <SearchInput value={search} onChange={setSearch} placeholder="Search users" />
          <SelectField
            className="select-field sm:w-48"
            value={selectedGroup}
            onChange={e => {
              // The [debouncedSearch, selectedGroup] effect refetches with the
              // new group - calling loadUsers() here too fired a second fetch
              // with the stale selectedGroup closure and raced the effect.
              setSelectedGroup(e.target.value);
              setPaginationToken(null);
            }}
          >
            <option value="">All Groups</option>
            {groups.map(g => (
              <option key={g.GroupName} value={g.GroupName}>{g.GroupName}</option>
            ))}
          </SelectField>
          <button onClick={() => setShowCreate(true)} className="btn-primary whitespace-nowrap"><FaUserPlus /> Create User</button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            <th onClick={() => handleSort('Username')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">Username {sortField === 'Username' ? <span className="text-cloudza-600">{sortDirection === 'asc' ? '↑' : '↓'}</span> : ''}</th>
            <th onClick={() => handleSort('Email')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">Email {sortField === 'Email' ? <span className="text-cloudza-600">{sortDirection === 'asc' ? '↑' : '↓'}</span> : ''}</th>
            <th onClick={() => handleSort('Status')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">Status {sortField === 'Status' ? <span className="text-cloudza-600">{sortDirection === 'asc' ? '↑' : '↓'}</span> : ''}</th>
            <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Groups</th>
            <th className="px-5 py-2.5 bg-[#fafafc]" />
          </tr>
        </thead>
        {usersLoading ? (
          <tbody>
            <TableRowsSkeleton columns={5} />
          </tbody>
        ) : (
          <tbody className="bg-white divide-y divide-gray-100">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan="5" className="py-10 px-6 text-center">
                  <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                    <FaUsers className="text-lg" />
                  </span>
                  <p className="mt-4 text-sm font-semibold text-gray-800">No users found</p>
                  <p className="mt-1 text-sm text-gray-400">Adjust your search or group filter, or create a new user.</p>
                </td>
              </tr>
            ) : paginated.map(u => {
              const email = u.Attributes?.find(a => a.Name === 'email')?.Value || '';
              const groups = u.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '';
              const isDisabled = u.Enabled === false;
              const status = u.UserStatus === 'UNCONFIRMED' ? 'Unconfirmed' : (isDisabled ? 'Disabled' : 'Enabled');
              return (
                <tr key={u.Username} className="hover:bg-cloudza-50/40 transition-colors">
                  <td className="px-5 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <IdentityTile name={u.Username} />
                      <span className="font-semibold text-gray-900">{u.Username}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap text-gray-700">{email}</td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    <span className={`status-badge ${status === 'Enabled' ? 'status-active' : status === 'Disabled' ? 'status-inactive' : 'status-pending'}`}>{status}</span>
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap">
                    {groups ? (
                      <div className="flex flex-wrap gap-1">
                        {groups.split(',').map(g => g.trim()).filter(Boolean).map(g => (
                          <Chip key={g} tint={GROUP_TINT[g] || 'gray'}>{g}</Chip>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 whitespace-nowrap text-sm">
                    <ActionDropdown
                      actions={[
                        isDisabled
                          ? { label: 'Enable', onClick: () => handleEnable(u) }
                          : { label: 'Disable', onClick: () => handleDisable(u) },
                        { label: 'Set Role', onClick: () => handleSetRole(u) },
                        { label: 'Edit', onClick: () => handleEdit(u) },
                        { label: 'Reset PW', onClick: () => handleReset(u) },
                        { label: 'Delete', onClick: () => handleDelete(u), className: 'text-red-600' },
                      ]}
                    />
                  </td>
                </tr>
            );
            })}
          </tbody>
        )}
      </table>
      </div>
      {paginationToken && (
        <div className="mt-4 text-center">
          <button onClick={() => loadUsers(paginationToken)} className="btn-outline"><FaChevronDown /> Load More</button>
        </div>
      )}

      {sorted.length > 0 && (
        <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 mt-4">
          <button onClick={() => handlePageChange(-1)} disabled={page === 1} className="btn-outline !py-1 !px-3 !text-xs">Prev</button>
          <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
          <button onClick={() => handlePageChange(1)} disabled={page >= totalPages} className="btn-outline !py-1 !px-3 !text-xs">Next</button>
        </div>
      )}
        </>
      )}

              {/* Agents Tab Content */}
      {activeTab === 'agents' && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-2 w-full">
            <SearchInput value={agentSearch} onChange={setAgentSearch} placeholder="Search agents by name or email" />
            <SelectField
              className="select-field sm:w-48"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
            >
              <option value="all">All Agents</option>
              <option value="with">With Email</option>
              <option value="without">Without Email</option>
            </SelectField>
          </div>

          {/* Updated Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="card">
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Total Agents</div>
              <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{agents.length}</div>
            </div>
            <div className="card">
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">General Managers</div>
              <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">
                {agents.filter(a => a.role === 'GM').length}
              </div>
            </div>
            <div className="card">
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Junior Managers</div>
              <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">
                {agents.filter(a => a.role === 'JM').length}
              </div>
            </div>
            <div className="card">
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">With Email</div>
              <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{agentsWithEmail}</div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-200">
                  <th onClick={() => handleAgentSort('name')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">
                    Agent Name {agentSortField === 'name' ? <span className="text-cloudza-600">{agentSortDirection === 'asc' ? '↑' : '↓'}</span> : ''}
                  </th>
                  {/* Added Role Column */}
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Role & Hierarchy</th>
                  <th onClick={() => handleAgentSort('email')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">
                    Email Address {agentSortField === 'email' ? <span className="text-cloudza-600">{agentSortDirection === 'asc' ? '↑' : '↓'}</span> : ''}
                  </th>
                  <th onClick={() => handleAgentSort('updatedAt')} className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc] cursor-pointer">
                    Last Updated {agentSortField === 'updatedAt' ? <span className="text-cloudza-600">{agentSortDirection === 'asc' ? '↑' : '↓'}</span> : ''}
                  </th>
                  <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Status</th>
                  <th className="px-5 py-2.5 bg-[#fafafc]" />
                </tr>
              </thead>
              {agentsLoading ? (
                <tbody>
                  <TableRowsSkeleton columns={6} />
                </tbody>
              ) : (
                <tbody className="bg-white divide-y divide-gray-100">
                  {sortedAgents.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-10 px-6 text-center">
                        <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                          <FaUserTie className="text-lg" />
                        </span>
                        <p className="mt-4 text-sm font-semibold text-gray-800">No agents found</p>
                        <p className="mt-1 text-sm text-gray-400">Agents appear here once calls have been processed. Try a different search or filter.</p>
                      </td>
                    </tr>
                  ) : paginatedAgents.map((agent) => {
                    const isRegistered = agent.email && existingUserEmails.has(agent.email.toLowerCase());

                    return (
                      <tr key={agent.name} className="hover:bg-cloudza-50/40 transition-colors">
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <IdentityTile name={agent.name} />
                            <span className="font-semibold text-gray-900">{agent.name}</span>
                          </div>
                        </td>
                        
                        {/* Role & Hierarchy Cell */}
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex flex-col">
                            <RoleBadge role={agent.role} />

                            {/* Show Hierarchy Links */}
                            {agent.role == 'JM' && agent.attachedGM && (
                              <span className="text-[10px] text-gray-500 mt-1">
                                Reports to: <b>{agent.attachedGM}</b>
                              </span>
                            )}
                            {(agent.role === 'General Manager' || agent.role === 'GM') && agent['attached-jm']?.length > 0 && (
                              <JMListDropdown jms={agent['attached-jm']} />
                            )}
                          </div>
                        </td>

                        <td className="px-5 py-3 whitespace-nowrap text-gray-700">
                          {agent.email || <span className="text-gray-400 italic">Not set</span>}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-500">
                          {agent.updatedAt ? new Date(agent.updatedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          {isRegistered ? (
                            <Chip tint="cloudza" className="whitespace-nowrap">Cognito User Active</Chip>
                          ) : agent.email ? (
                            <Chip tint="green" className="gap-1.5 whitespace-nowrap">
                              <FaEnvelope style={{ fontSize: '0.7rem' }} />
                              Will receive reports
                            </Chip>
                          ) : (
                            <Chip tint="gray" className="gap-1.5 whitespace-nowrap">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                              No reports
                            </Chip>
                          )}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-right space-x-2">
                          <button 
                            onClick={() => setEditAgent(agent)} 
                            disabled={isRegistered}
                            className={`btn-outline !py-1 !px-3 !text-xs ${isRegistered ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <FaPen />
                            {agent.email || agent.role ? 'Edit Details' : 'Set Details'}
                          </button>

                          {agent.email && (
                            <button 
                              onClick={() => setPreFillData({ 
                                username: agent.name, 
                                email: agent.email 
                              })} 
                              disabled={isRegistered}
                              className={`btn-primary !py-1 !px-3 !text-xs ${isRegistered ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {isRegistered ? 'User Exists' : <><FaUserPlus /> Create User</>}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
          {sortedAgents.length > 0 && (
            <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 mt-4">
              <button onClick={() => handleAgentPageChange(-1)} disabled={agentPage === 1} className="btn-outline !py-1 !px-3 !text-xs">Prev</button>
              <span className="text-xs text-gray-400">Page {agentPage} of {agentTotalPages}</span>
              <button onClick={() => handleAgentPageChange(1)} disabled={agentPage >= agentTotalPages} className="btn-outline !py-1 !px-3 !text-xs">Next</button>
            </div>
          )}
          <AgentEmailModal
            open={!!editAgent}
            agent={editAgent}
            gmUsername={generalManagers}
            onClose={() => setEditAgent(null)}
            onSubmit={handleUpdateAgentEmail}
          />
        </>
      )}

      {/* Activity Log Tab Content */}
      {activeTab === 'activity' && (
        <div className="space-y-4">
          <div className="max-w-sm">
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">Admin</label>
            <SelectField
              className="select-field w-full"
              value={selectedActor}
              onChange={(e) => handleSelectActor(e.target.value)}
              disabled={actorsLoading}
            >
              <option value="">
                {actorsLoading ? 'Loading admins...' : 'Select an admin'}
              </option>
              {actors.map((a) => (
                <option key={a.actorEmail} value={a.actorEmail}>
                  {a.actorEmail} ({a.activityCount} action{a.activityCount === 1 ? '' : 's'})
                </option>
              ))}
            </SelectField>
          </div>

          {!actorsLoading && actors.length === 0 && (
            <div className="py-10 px-6 text-center">
              <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                <FaHistory className="text-lg" />
              </span>
              <p className="mt-4 text-sm font-semibold text-gray-800">No activity recorded yet</p>
              <p className="mt-1 text-sm text-gray-400">Entries appear here once an admin edits an agent's email or role on the "Agent Emails" tab.</p>
            </div>
          )}

          {selectedActor && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">When</th>
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Agent</th>
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Email</th>
                    <th className="px-5 py-2.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider bg-[#fafafc]">Role</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {activitiesLoading ? (
                    <TableRowsSkeleton columns={4} />
                  ) : activities.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="py-10 px-6 text-center">
                        <span className="mx-auto w-12 h-12 rounded-2xl bg-cloudza-50 text-cloudza-700 flex items-center justify-center">
                          <FaHistory className="text-lg" />
                        </span>
                        <p className="mt-4 text-sm font-semibold text-gray-800">No activity recorded for this admin</p>
                        <p className="mt-1 text-sm text-gray-400">This admin hasn't made any agent email or role changes yet.</p>
                      </td>
                    </tr>
                  ) : activities.map((entry) => (
                    <tr key={entry.id} className="hover:bg-cloudza-50/40 transition-colors">
                      <td className="px-5 py-3 whitespace-nowrap tabular-nums text-gray-500 text-xs">
                        {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <IdentityTile name={entry.agentName} />
                          <span className="font-semibold text-gray-900">{entry.agentName}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap text-gray-700">{entry.details?.email || <span className="text-gray-400 italic">removed</span>}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        {/* Older entries predate before-value capture and only carry the new role. */}
                        {entry.details?.previousRole && entry.details.previousRole !== entry.details?.role ? (
                          <span className="inline-flex items-center gap-1.5">
                            <RoleBadge role={entry.details.previousRole} />
                            <span className="text-gray-400" aria-hidden="true">→</span>
                            <RoleBadge role={entry.details?.role} />
                          </span>
                        ) : (
                          <RoleBadge role={entry.details?.role} />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <UserFormModal
        open={showCreate || !!preFillData}
        title="Create User"
        groups={groups}
        initial={preFillData || {}}
        onClose={() => {
            setShowCreate(false);
            setPreFillData(null);
          }}
        onSubmit={async (data) => {
          try {
            await createUser({
              username: data.username,
              email: data.email,
              temporaryPassword: data.password || undefined,
              groups: data.groups
            });
            setConfirmation({ 
                    open: true, 
                    title: 'User Created', 
                    message: `User ${data.username} was created successfully.` 
                  });
            setShowCreate(false);
            setPreFillData(null);
            loadUsers();
          } catch (err) {
            console.error('Failed to create user', err);
            // Surface the backend's reason — "Username already exists" in
            // particular — instead of a generic failure.
            const backendMessage = err?.message || '';
            const message = /exist/i.test(backendMessage)
              ? `A user named "${data.username}" already exists. Choose a different username, or update the existing user instead.`
              : backendMessage || "Couldn't create user";
            setConfirmation({ open: true, title: "Couldn't Create User", message });
          }
        }}
      />

      {editUserModal && (
        <UserFormModal
          open={!!editUserModal}
          title={`Edit ${editUserModal.Username}`}
          groups={groups}
          initial={{
            username: editUserModal.Username,
            email: editUserModal.Attributes?.find(a => a.Name === 'email')?.Value || '',
            groups: (editUserModal.Attributes?.find(a => a.Name === 'custom:groups')?.Value || '').split(',').filter(Boolean)
          }}
          onClose={() => setEditUserModal(null)}
          onSubmit={async (data) => {
            try {
              await updateUser(editUserModal.Username, { email: data.email });
              setEditUserModal(null);
              setConfirmation({ open: true, title: 'User Updated', message: 'User updated successfully.' });
              loadUsers();
            } catch (err) {
              console.error('Failed to update user', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't update user" });
            }
          }}
        />
      )}

      {resetUserModal && (
        <ResetPasswordModal
          open={!!resetUserModal}
          username={resetUserModal.Username}
          onClose={() => setResetUserModal(null)}
          onSubmit={async (pw) => {
            try {
              await resetUserPassword(resetUserModal.Username, pw);
              setResetUserModal(null);
              setConfirmation({ open: true, title: 'Password Reset', message: 'Temporary password set.' });
            } catch (err) {
              console.error('Failed to reset password', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't reset password" });
            }
          }}
        />
      )}

      {roleModal && (
        <RoleModal
          open={!!roleModal}
          username={roleModal.Username}
          onClose={() => setRoleModal(null)}
          onSubmit={async (role) => {
            try {
              await setUserRole(roleModal.Username, role);
              setRoleModal(null);
              setConfirmation({ open: true, title: 'Role Updated', message: 'Role updated successfully.' });
              loadUsers();
            } catch (err) {
              console.error('Failed to set role', err);
              setConfirmation({ open: true, title: 'Error', message: "Couldn't set role" });
            }
          }}
        />
      )}

      {confirmAction && (
        <Modal open={!!confirmAction} onClose={() => setConfirmAction(null)} title={confirmAction.title}>
          <p className="mb-4">{confirmAction.message}</p>
          <div className="flex flex-wrap justify-end gap-2">
            <button onClick={() => setConfirmAction(null)} className="btn-outline"><FaTimes /> Cancel</button>
            <button
              onClick={async () => {
                await confirmAction.onConfirm();
              }}
              className="btn-primary"
            >
              <FaCheck />
              Confirm
            </button>
          </div>
        </Modal>
      )}
      <ConfirmationModal
        open={confirmation.open}
        title={confirmation.title}
        message={confirmation.message}
        onClose={() => setConfirmation({ open: false, title: '', message: '' })}
      />
      <DeleteConfirmationModal
        open={!!deleteUserName}
        itemName={deleteUserName || ''}
        onClose={() => setDeleteUserName('')}
        onConfirm={confirmDeleteUser}
      />
    </div>
  );
};

// The /agents route's page — same component, agent-facing section.
export const AgentManagement = (props) => <UserManagement {...props} section="agents" />;

export default UserManagement;
